/**
 * P1-001 FULL COMPLIANCE: Cookie Consent Service
 *
 * ENFORCEMENT LAYERS:
 *   1. LOCAL: PostHog script NOT in index.html. Dynamically injected only after consent.
 *   2. SERVER: Every consent action sent to POST /api/consent (immutable audit log).
 *   3. VERSION: Consent is valid ONLY for current policy version. Backend is authority.
 *   4. EFFECTIVE: GET /api/consent/effective returns single deterministic effective state.
 *
 * VERSION MODEL:
 *   - CONSENT_VERSION constant here MUST match CURRENT_CONSENT_VERSION in server.py.
 *   - When version changes: getConsent() returns null → banner re-appears → user re-consents.
 *   - Backend GET /api/consent/effective returns has_consent=false for stale versions.
 *
 * MERGE:
 *   - Anonymous users get anonymous_id (persisted in localStorage).
 *   - On login, mergeConsentOnLogin() links anonymous records to person_id.
 */

const STORAGE_KEY = 'treatx_cookie_consent';
const CONSENT_VERSION = '1.0';
const ANON_ID_KEY = 'treatx_consent_anon_id';

const POSTHOG_KEY = 'phc_yJW1VjHGGwmCbbrtczfqqNxgBDbhlhOWcdzcIJEOTFE';
const POSTHOG_HOST = 'https://us.i.posthog.com';
const POSTHOG_SCRIPT_ID = 'treatx-posthog-script';

const API_BASE = process.env.REACT_APP_BACKEND_URL
  ? `${process.env.REACT_APP_BACKEND_URL}/api`
  : '/api';

// ── Privacy-minimised analytics configuration ────────────────────────────
// TREATX-COOKIE-ANALYTICS-MINIMISATION-01 (health marketplace: fail closed)
//
// `CONSENT_MAX_AGE_DAYS` mirrors CONSENT_MAX_AGE_DAYS in server.py: consent
// expires after 12 months even when the policy version is unchanged.
const CONSENT_MAX_AGE_DAYS = 365;
const CONSENT_MAX_AGE_MS = CONSENT_MAX_AGE_DAYS * 24 * 60 * 60 * 1000;

// Path segments that look like an identifier (hex id, uuid, long slug,
// numeric id) are replaced so booking / request / application / person /
// file / message identifiers never leave the device inside a URL.
const ID_SEGMENT_RE = /^(?:[0-9a-fA-F-]{16,}|[0-9]{4,}|[A-Za-z0-9_-]{16,})$/;

function maskPathIds(path) {
  return (path || '')
    .split('/')
    .map((seg) => (ID_SEGMENT_RE.test(seg) ? ':id' : seg))
    .join('/');
}

/**
 * Strip query strings, fragments' query strings and identifier segments from
 * any URL before it can reach analytics. TreatX runs on a hash router, so the
 * route lives in `location.hash` and must be sanitised too.
 * Exported for tests.
 */
export function sanitizeUrlForAnalytics(raw) {
  try {
    const u = new URL(String(raw), window.location.origin);
    const hashRoute = (u.hash || '').replace(/^#/, '').split('?')[0].split('&')[0];
    const out = u.origin + maskPathIds(u.pathname);
    return hashRoute ? `${out}#${maskPathIds(hashRoute)}` : out;
  } catch {
    return '';
  }
}

const URL_PROPERTY_KEYS = new Set([
  '$current_url', '$initial_current_url', '$pathname', '$initial_pathname',
  '$referrer', '$initial_referrer', '$referring_domain',
  '$initial_referring_domain', '$host', '$initial_host',
]);

// Free-text / content-bearing properties that must never be transmitted.
const PROPERTY_DENYLIST = [
  '$el_text', '$elements', '$elements_chain', '$selected_content',
  '$set', '$set_once', '$autocapture_disabled_server_side',
];

/**
 * Last-line sanitiser applied to every outgoing event. Exported for tests.
 */
export function sanitizeAnalyticsProperties(properties) {
  const out = { ...(properties || {}) };
  for (const key of Object.keys(out)) {
    if (key.includes('pathname')) {
      out[key] = typeof out[key] === 'string'
        ? maskPathIds(String(out[key]).split('?')[0].split('#')[0])
        : null;
    } else if (URL_PROPERTY_KEYS.has(key)) {
      out[key] = typeof out[key] === 'string' ? sanitizeUrlForAnalytics(out[key]) : null;
    } else if (key === '$search_engine_keyword' || key.startsWith('$el_')) {
      delete out[key];
    }
  }
  return out;
}

export const POSTHOG_INIT_CONFIG = {
  api_host: POSTHOG_HOST,
  person_profiles: 'identified_only',
  // 1. Session/screen recording can NEVER be switched on from the PostHog
  //    project dashboard: the client refuses it locally.
  disable_session_recording: true,
  session_recording: false,
  // 2. No DOM/autocapture collection at all — no element text, no input
  //    values, no form content, no health-related DOM.
  autocapture: false,
  capture_dead_clicks: false,
  rageclick: false,
  capture_heatmaps: false,
  capture_performance: false,
  disable_surveys: true,
  disable_external_dependency_loading: true,
  // Aggregate page analytics only, with sanitised URLs.
  capture_pageview: true,
  capture_pageleave: false,
  property_denylist: PROPERTY_DENYLIST,
  sanitize_properties: sanitizeAnalyticsProperties,
};

// ── Anonymous ID ─────────────────────────────────────────────────────────

function getOrCreateAnonymousId() {
  let anonId = localStorage.getItem(ANON_ID_KEY);
  if (!anonId) {
    anonId = 'anon_' + crypto.randomUUID().replace(/-/g, '').slice(0, 16);
    localStorage.setItem(ANON_ID_KEY, anonId);
  }
  return anonId;
}

export function getAnonymousId() {
  return localStorage.getItem(ANON_ID_KEY) || getOrCreateAnonymousId();
}

// ── Local Consent State ──────────────────────────────────────────────────

export function getConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed.categories || !parsed.timestamp) return null;
    // VERSION GATE: reject stale consent
    if (parsed.version !== CONSENT_VERSION) return null;
    // AGE GATE (TREATX-COOKIE-CONSENT-12-MONTH-01): consent is valid for a
    // maximum of 12 months, after which the banner must reappear. Same rule
    // as the backend authority (server.py CONSENT_MAX_AGE_DAYS).
    const ts = Date.parse(parsed.timestamp);
    if (!Number.isFinite(ts) || Date.now() - ts > CONSENT_MAX_AGE_MS) return null;
    return parsed;
  } catch {
    return null;
  }
}

export function hasConsent(category) {
  if (category === 'essential') return true;
  const consent = getConsent();
  if (!consent) return false;
  return consent.categories[category] === true;
}

/**
 * Save consent locally AND to the server.
 */
export function saveConsent(categories, source = 'banner') {
  const consent = {
    categories: {
      essential: true,
      analytics: !!categories.analytics,
      marketing: !!categories.marketing,
    },
    timestamp: new Date().toISOString(),
    version: CONSENT_VERSION,
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(consent));
  window.dispatchEvent(new CustomEvent('treatx-consent-changed', { detail: consent }));
  enforceConsent(consent);
  sendConsentToServer(consent.categories, source);
  return consent;
}

export function acceptAll(source = 'banner') {
  // TREATX-COOKIE-MARKETING-CATEGORY-REMOVAL-01 — TreatX loads no
  // advertising / retargeting / social tracking technology, so "Accept all"
  // must never record marketing consent. The field is retained (always
  // false) for backward compatibility with historical consent evidence.
  return saveConsent({ essential: true, analytics: true, marketing: false }, source);
}

export function rejectNonEssential(source = 'banner') {
  return saveConsent({ essential: true, analytics: false, marketing: false }, source);
}

export function clearConsent() {
  localStorage.removeItem(STORAGE_KEY);
  disableAnalytics();
  disableMarketing();
  window.dispatchEvent(new CustomEvent('treatx-consent-changed', { detail: null }));
}

// ── Server-Side Audit ────────────────────────────────────────────────────

function getAuthToken() {
  // FIND-H-08 · post-cutover: browser auth is the HttpOnly `treatx_auth`
  // cookie. No JS-readable JWT exists. This helper is retained as a
  // no-op stub for legacy call sites — consent endpoints receive the
  // canonical identity via the cookie sent automatically with
  // `credentials: 'include'` (installed globally in SoftLaunchGate).
  return null;
}

async function sendConsentToServer(categories, source) {
  try {
    const anonymousId = getOrCreateAnonymousId();
    const headers = { 'Content-Type': 'application/json' };

    await fetch(`${API_BASE}/consent`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: JSON.stringify({
        anonymous_id: anonymousId,
        consent: {
          analytics: !!categories.analytics,
          marketing: !!categories.marketing,
        },
        version: CONSENT_VERSION,
        source,
      }),
    });
  } catch (err) {
    console.warn('[CookieConsent] Backend audit failed:', err.message);
  }
}

/**
 * Merge anonymous consent records to person_id after login.
 */
export async function mergeConsentOnLogin() {
  try {
    const anonymousId = localStorage.getItem(ANON_ID_KEY);
    if (!anonymousId) return;
    const token = getAuthToken();
    if (!token) return;

    await fetch(`${API_BASE}/consent/merge`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ anonymous_id: anonymousId }),
    });
  } catch (err) {
    console.warn('[CookieConsent] Merge failed:', err.message);
  }
}

/**
 * P1-013: Fetch effective consent from backend.
 * Returns { has_consent, version, consent, source, timestamp, is_valid }
 * or null on error.
 */
export async function fetchEffectiveConsent() {
  try {
    const headers = {};
    const token = getAuthToken();
    if (token) {
    }

    const anonymousId = getAnonymousId();
    const params = token ? '' : `?anonymous_id=${encodeURIComponent(anonymousId)}`;

    const resp = await fetch(`${API_BASE}/consent/effective${params}`, { headers });
    if (!resp.ok) return null;
    return await resp.json();
  } catch {
    return null;
  }
}

/**
 * P1-012: Fetch current consent version from backend.
 */
export async function fetchConsentVersion() {
  try {
    const resp = await fetch(`${API_BASE}/consent/version`);
    if (!resp.ok) return null;
    return await resp.json();
  } catch {
    return null;
  }
}

/**
 * Validate local consent against backend.
 * If backend says has_consent=false (version mismatch or no record),
 * clear local consent → banner re-appears.
 *
 * Called during initConsentEnforcement after enforcing local state.
 */
export async function validateConsentWithBackend() {
  const effective = await fetchEffectiveConsent();
  if (!effective) return; // Network error — trust local state

  if (!effective.has_consent || !effective.is_valid) {
    // Backend says no valid consent → clear local state → banner re-shows
    const localConsent = getConsent();
    if (localConsent) {
      localStorage.removeItem(STORAGE_KEY);
      disableAnalytics();
      disableMarketing();
      window.dispatchEvent(new CustomEvent('treatx-consent-changed', { detail: null }));
    }
  }
}

// ── PostHog Enforcement ──────────────────────────────────────────────────

export function enforceConsent(consent) {
  if (!consent) {
    disableAnalytics();
    disableMarketing();
    return;
  }
  if (consent.categories.analytics) {
    enableAnalytics();
  } else {
    disableAnalytics();
  }
  if (consent.categories.marketing) {
    enableMarketing();
  } else {
    disableMarketing();
  }
}

function enableAnalytics() {
  window.__treatx_analytics_enabled = true;
  if (window.posthog && typeof window.posthog.opt_in_capturing === 'function') {
    window.posthog.opt_in_capturing();
    return;
  }
  if (document.getElementById(POSTHOG_SCRIPT_ID)) return;
  injectPostHog();
}

function disableAnalytics() {
  window.__treatx_analytics_enabled = false;
  // TREATX-COOKIE-WITHDRAWAL-DEVICE-STATE-01 — stop capture AND remove the
  // PostHog device state. Supported API first (`opt_out_capturing` +
  // `reset(true)` which drops the stored distinct_id / person state), then a
  // targeted sweep of the PostHog persistence keys only. The canonical TreatX
  // consent evidence (`treatx_cookie_consent`) and the anonymous audit
  // identifier (`treatx_consent_anon_id`) are deliberately preserved.
  const ph = window.posthog;
  if (ph) {
    try {
      // Order matters: switch persistence to memory FIRST so the already
      // loaded client can no longer write cookies / web storage, THEN
      // opt out and reset (supported APIs), THEN sweep what is left on
      // the device. Without the memory switch PostHog re-persists a fresh
      // distinct_id immediately after `reset()`.
      if (typeof ph.set_config === 'function') ph.set_config({ persistence: 'memory' });
      if (typeof ph.opt_out_capturing === 'function') ph.opt_out_capturing();
      if (typeof ph.reset === 'function') ph.reset(true);
    } catch { /* PostHog stub not fully loaded — cleanup below still runs */ }
  }
  clearPostHogDeviceState();
  // Second sweep on the next macrotask: PostHog flushes persistence
  // asynchronously, so anything written during teardown is removed too.
  setTimeout(clearPostHogDeviceState, 0);
  const existing = document.getElementById(POSTHOG_SCRIPT_ID);
  if (existing) existing.remove();
}

/**
 * Remove PostHog-owned cookies and web-storage entries for this project.
 * Only keys PostHog itself creates are touched (`ph_*`, `__ph*`). Exported
 * for tests.
 */
export function clearPostHogDeviceState() {
  // Everything PostHog owns is removed EXCEPT the opt-out marker
  // `__ph_opt_in_out_<key>`: that entry carries no identifier, it only
  // records "this device opted out" and keeps capture suppressed if the
  // client is ever loaded again before the user re-consents.
  const isPostHogKey = (k) => !!k
    && (k.startsWith('ph_') || k.startsWith('__ph'))
    && !k.startsWith('__ph_opt_in_out');
  try {
    for (const store of [window.localStorage, window.sessionStorage]) {
      if (!store) continue;
      const doomed = [];
      for (let i = 0; i < store.length; i += 1) {
        const k = store.key(i);
        if (isPostHogKey(k)) doomed.push(k);
      }
      doomed.forEach((k) => store.removeItem(k));
    }
  } catch { /* storage unavailable (private mode) */ }
  try {
    const names = document.cookie.split(';').map((c) => c.split('=')[0].trim());
    names.filter(isPostHogKey).forEach((name) => {
      const base = `${encodeURIComponent(name)}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      document.cookie = base;
      document.cookie = `${base}; domain=${window.location.hostname}`;
      document.cookie = `${base}; domain=.${window.location.hostname}`;
    });
  } catch { /* cookies unavailable */ }
}

function enableMarketing() {
  window.__treatx_marketing_enabled = true;
}

function disableMarketing() {
  window.__treatx_marketing_enabled = false;
}

function injectPostHog() {
  !(function (t, e) {
    var o, n, p, r;
    e.__SV ||
      ((window.posthog = e),
      (e._i = []),
      (e.init = function (i, s, a) {
        function g(t, e) {
          var o = e.split('.');
          2 === o.length && ((t = t[o[0]]), (e = o[1])),
            (t[e] = function () {
              t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
            });
        }
        ((p = t.createElement('script')).type = 'text/javascript'),
          (p.crossOrigin = 'anonymous'),
          (p.async = !0),
          (p.id = POSTHOG_SCRIPT_ID),
          (p.src =
            s.api_host.replace('.i.posthog.com', '-assets.i.posthog.com') +
            '/static/array.js'),
          (r = t.getElementsByTagName('script')[0]).parentNode.insertBefore(p, r);
        var u = e;
        for (
          void 0 !== a ? (u = e[a] = []) : (a = 'posthog'),
            u.people = u.people || [],
            u.toString = function (t) {
              var e = 'posthog';
              return 'posthog' !== a && (e += '.' + a), t || (e += ' (stub)'), e;
            },
            u.people.toString = function () {
              return u.toString(1) + '.people (stub)';
            },
            o =
              'init me ws ys ps bs capture je Di ks register register_once register_for_session unregister unregister_for_session Ps getFeatureFlag getFeatureFlagPayload isFeatureEnabled reloadFeatureFlags updateEarlyAccessFeatureEnrollment getEarlyAccessFeatures on onFeatureFlags onSurveysLoaded onSessionId getSurveys getActiveMatchingSurveys renderSurvey canRenderSurvey canRenderSurveyAsync identify setPersonProperties group resetGroups setPersonPropertiesForFlags resetPersonPropertiesForFlags setGroupPropertiesForFlags resetGroupPropertiesForFlags reset get_distinct_id getGroups get_session_id get_session_replay_url alias set_config startSessionRecording stopSessionRecording sessionRecordingStarted captureException loadToolbar get_property getSessionProperty Es $s createPersonProfile Is opt_in_capturing opt_out_capturing has_opted_in_capturing has_opted_out_capturing clear_opt_in_out_capturing Ss debug xs getPageViewId captureTraceFeedback captureTraceMetric'.split(
                ' ',
              ),
            n = 0;
          n < o.length;
          n++
        )
          g(u, o[n]);
        e._i.push([i, s, a]);
      }),
      (e.__SV = 1));
  })(document, window.posthog || []);

  window.posthog.init(POSTHOG_KEY, POSTHOG_INIT_CONFIG);
}

/**
 * Initialize consent enforcement on app load.
 *
 * 1. Generate anonymous_id if needed
 * 2. Check local consent (version-gated)
 * 3. Enforce locally (PostHog inject/block)
 * 4. Validate against backend asynchronously (version check)
 */
export function initConsentEnforcement() {
  getOrCreateAnonymousId();
  const consent = getConsent();
  if (consent) {
    enforceConsent(consent);
  }
  // Async validation: if backend says stale version, clear local → re-prompt
  validateConsentWithBackend();
}
